import React, { useState, useEffect } from 'react';

import { Grid, Box, Divider, Typography } from '@material-ui/core';

import Header from 'on_web/components/shared/Menu/Header/Header';
import ReportsBottomNavigation from 'on_web/components/shared/ReportsBottomNavigation/ReportsBottomNavigation';
import RedirectPanelWithIcon from 'on_web/components/shared/RedirectPanelWithIcon/RedirectPanelWithIcon';
import PageItemContainer from 'on_web/components/shared/Common/PageItemContainer/PageItemContainer';
import ReportsOpenTextSection from 'on_web/components/shared/ReportsOpenTextSection/ReportsOpenTextSection';
import DesktopMenu from 'on_web/components/shared/Menu/DesktopMenu/DesktopMenu';
import RisksOnHumanBody from 'on_web/components/shared/RisksOnHumanBody/RisksOnHumanBody';
import DnaBasedRiskSummary from 'on_web/components/shared/DnaBasedRiskSummary/DnaBasedRiskSummary';
import MockedDnaBasedRiskSummary from 'on_web/components/shared/DnaBasedRiskSummary/MockedDnaBasedRiskSummary';

import useUsersApi from 'on_web/hooks/useUsersApi';
import useCheckScreenSize from 'on_web/hooks/useCheckScreenSize';
import useInterval from 'on_web/hooks/useInterval';
import { useTranslation } from 'react-i18next';

import { APP_PRIMARY } from 'colours';
import {
  getDnaRisks,
  parseDnaTotalRisk,
  userCanSeeDnaContent,
  isImputationCompleted,
  isImputationInError,
  isImputationInProgress,
  userCanSeeFullDna,
  isDnaUploaded,
  isGenotypeInvalid,
} from 'on_web/helpers/risksHelper';

import {
  ROUTE_ASSESSMENT_DNA_TRAIT_GROUP,
  ROUTE_ONBOARDING_DNA,
  ROUTE_ASSESSMENT_HOME,
} from 'routes';
import { reportsBottomNavigationOptions } from 'on_web/helpers/redirectHelper';
import { getUserMetadata } from '../pce_legacy/src/helpers/clientStorageHelper';
import { mapRoutesToMenuItems } from 'on_web/helpers/menuItems';
import RiskReport from 'on_web/components/shared/RiskReport/RiskReport';
import CustomInfoPopup from 'on_web/components/shared/Popup/CustomInfoPopup';
import CustomDialog from 'on_web/components/shared/CustomDialog/CustomDialog';
import ErrorMessageTile from 'on_web/components/shared/ErrorMessageTile/ErrorMessageTile';
import MockedDnaRiskExplanation from 'on_web/components/shared/DnaBasedRiskSummary/MockedDnaRiskExplanation';

export default function ReportsDnaAssessment(props) {
  const [isDesktop] = useCheckScreenSize();
  const [openBuyDialog, setOpenBuyDialog] = useState(undefined);
  const [genotypeLoaded, setGenotypeLoaded] = useState(undefined);
  const [
    {
      getSpareAssessments,
      errorSpareAssessments,
      getGenotype,
      getUserData,
      responseReports,
      getReports,
      getProductsData,
    },
  ] = useUsersApi();
  const [genotypeData, setGenotypeData] = useState(undefined);
  const [responseHealthBasedRisks, setResponseHealthBasedRisks] = useState(
    null
  );
  const [responseLikelihood, setResponseLikelihood] = useState(null);
  const [responseDnaBasedRisks, setResponseDnaBasedRisks] = useState(null);
  const [dnaTotalRisk, setDnaTotalRisk] = useState(undefined);
  const [responseBloodType, setResponseBloodType] = useState(null);
  const [userGroups, setUserGroups] = useState(undefined);
  const [productData, setProductData] = useState(null);

  useEffect(() => {
    getUserData()
      .then(response => {
        if (response && response.groups) {
          setUserGroups(response.groups);
          if (userCanSeeDnaContent(response.groups)) {
            setOpenBuyDialog(false);
          } else {
            setOpenBuyDialog(true);
          }
        }
      })
      .then(() => getGenotype())
      .then(response => {
        if (
          response == null ||
          Object.keys(response).length === 0 ||
          response.detail === 'Genotype not defined for the profile'
        ) {
          setOpenBuyDialog(true);
        } else {
          setGenotypeData({
            genotype_status: response.status,
            imputed_genotype_status: response.imputed_genotype
              ? response.imputed_genotype.status
              : null,
          });
        }
        setGenotypeLoaded(true);
      })
      .catch(err => {
        setOpenBuyDialog(true);
      });

    getReports();
  }, [getGenotype, getReports, getUserData]);

  useEffect(() => {
    if (
      props.location.state &&
      props.location.state.healthBasedRisks &&
      props.location.state.infectionLikelihood &&
      props.location.state.dnaBasedRisks &&
      props.location.state.bloodType
    ) {
      setResponseHealthBasedRisks(props.location.state.healthBasedRisks);
      setResponseLikelihood(props.location.state.infectionLikelihood);
      setResponseDnaBasedRisks(props.location.state.dnaBasedRisks);
      setDnaTotalRisk(parseDnaTotalRisk(props.location.state.dnaBasedRisks, t));
      setResponseBloodType(props.location.state.bloodType);
    } else {
      getSpareAssessments().then(resp => {
        if (resp) {
          setResponseHealthBasedRisks(resp.health_based_severity);
          setResponseLikelihood(resp.past_infection_likelihood);
          setResponseDnaBasedRisks(resp.dna_based_severity);
          setDnaTotalRisk(parseDnaTotalRisk(resp.dna_based_severity, t));
          setResponseBloodType(resp.blood_type);
        }
      });
    }
  }, [getSpareAssessments]);

  useInterval(() => {
    if (responseReports == null || responseReports.download_url == null) {
      getReports();
    }
  }, 5000);

  useEffect(() => {
    getProductsData()
      .then(resp => {
        if (resp != null && resp.length !== 0) {
          resp.forEach(product => {
            if (
              product.title != null &&
              product.price != null &&
              product.sku === 'dna-blood-analysis'
            ) {
              setProductData({
                title: product.title,
                price: product.price,
              });
            }
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  }, [getProductsData]);

  const { t } = useTranslation();

  const dnaTraitsAssets = getDnaRisks(APP_PRIMARY, '60px');

  const getStateToTransfer = () => {
    return {
      ...props.location.state,
      healthBasedRisks: responseHealthBasedRisks,
      infectionLikelihood: responseLikelihood,
      dnaBasedRisks: responseDnaBasedRisks,
      bloodType: responseBloodType,
    };
  };

  const getChartData = () => {
    if (responseDnaBasedRisks == null) {
      return [];
    }

    const bloodBiomarkersGroup = responseDnaBasedRisks['blood_biomarkers'];
    const cardiacGroup = responseDnaBasedRisks['cardiac'];
    const diabetesGroup = responseDnaBasedRisks['diabetes'];
    const respiratoryGroup = responseDnaBasedRisks['respiratory'];

    const data = [];
    data.push(
      {
        label: bloodBiomarkersGroup.name.replace(' Predisposition Group', ''),
        subLabel: bloodBiomarkersGroup.assessment_label,
        value: bloodBiomarkersGroup.assessment_level,
        code: bloodBiomarkersGroup.code,
        isDna: true,
      },
      {
        label: 'Blood Type',
        subLabel: '',
        value: responseBloodType.level,
        code: 'blood_type',
        isDna: true,
      }
    );

    if (userCanSeeFullDna(userGroups)) {
      data.push(
        {
          label: cardiacGroup.name.replace(' Predisposition Group', ''),
          subLabel: cardiacGroup.assessment_label,
          value: cardiacGroup.assessment_level,
          code: cardiacGroup.code,
          isDna: true,
        },
        {
          label: diabetesGroup.name.replace(' Predisposition Group', ''),
          subLabel: diabetesGroup.assessment_label,
          value: diabetesGroup.assessment_level,
          code: diabetesGroup.code,
          isDna: true,
        },
        {
          label: respiratoryGroup.name.replace(' Predisposition Group', ''),
          subLabel: respiratoryGroup.assessment_label,
          value: respiratoryGroup.assessment_level,
          code: respiratoryGroup.code,
          isDna: true,
        }
      );
    }
    return data;
  };

  const getMockedChartData = () => {
    return [
      {
        label: 'Blood Type',
        subLabel: '',
        value: 1,
        code: 'blood_type',
        isDna: true,
      },
      {
        label: 'Blood Biomarkers',
        subLabel: '',
        value: 2,
        code: 'blood_biomarkers',
        isDna: true,
      },
    ];
  };

  return (
    <React.Fragment>
      <div style={{ display: 'flex' }}>
        {errorSpareAssessments ? (
          <CustomDialog
            open={errorSpareAssessments}
            onCloseButton={() => {
              window.location = '/';
            }}
            style={{ marginLeft: isDesktop ? '300px' : '0' }}
          >
            <ErrorMessageTile
              level="error"
              title={t('common.errorMessages.general.title')}
              message={t('common.errorMessages.general.message')}
              actionText={t('common.errorMessages.general.reloadAction')}
              onButtonClick={() => {
                window.location = '/';
              }}
            ></ErrorMessageTile>
          </CustomDialog>
        ) : (
          <>
            <DesktopMenu
              id={2}
              stateToTransferOnNavigation={getStateToTransfer()}
              hasDnaProcessed={isImputationCompleted(genotypeData)}
              hasFullDna={userCanSeeFullDna(userGroups)}
            />
            <Grid
              container={isDesktop}
              direction="row"
              justify="center"
              alignItems="center"
              style={{
                width: `calc(100% - ${isDesktop ? 300 : 0}px)`,
              }}
            >
              <PageItemContainer>
                {!isDesktop && (
                  <Header
                    squeeze
                    title={t('reporting.myAssessment.dnaBasedAssessment')}
                    secondaryHeader
                    hasShareAction
                    mainMenuItems={t('common.menu.mainItems', {
                      returnObjects: true,
                    })}
                    extraMenuItems={mapRoutesToMenuItems(getUserMetadata())}
                  />
                )}

                {genotypeData &&
                  userGroups &&
                  userCanSeeDnaContent(userGroups) &&
                  isImputationInError(genotypeData) && (
                    <CustomDialog
                      open={true}
                      onCloseButton={() => {
                        window.location = '/';
                      }}
                      style={{ marginLeft: isDesktop ? '300px' : '0' }}
                    >
                      <ErrorMessageTile
                        level="error"
                        title={'ERROR'}
                        message={t(
                          'common.errorMessages.genotypeImputationError:message'
                        )}
                        actionText={'OK'}
                        onButtonClick={() => {
                          props.history.push({
                            pathname: ROUTE_ASSESSMENT_HOME,
                            state: { returningVisit: true },
                          });
                        }}
                      ></ErrorMessageTile>
                    </CustomDialog>
                  )}

                {genotypeData &&
                  userGroups &&
                  userCanSeeDnaContent(userGroups) &&
                  isGenotypeInvalid(genotypeData) && (
                    <CustomDialog
                      open={true}
                      style={{ marginLeft: isDesktop ? '300px' : '0' }}
                    >
                      <ErrorMessageTile
                        level="error"
                        title={'ERROR'}
                        message={t(
                          'common.errorMessages.genotypeProcessingError:message'
                        )}
                        actionText={'OK'}
                        onButtonClick={() => {
                          props.history.push({
                            pathname: ROUTE_ONBOARDING_DNA,
                            state: { returningVisit: true },
                          });
                        }}
                      ></ErrorMessageTile>
                    </CustomDialog>
                  )}

                {genotypeLoaded &&
                  userGroups &&
                  userCanSeeDnaContent(userGroups) &&
                  (isDnaUploaded(genotypeData) === false ||
                    (genotypeData && isImputationInProgress(genotypeData))) && (
                    <CustomInfoPopup
                      open={true}
                      text={
                        genotypeData && isImputationInProgress(genotypeData)
                          ? t(
                              'common.errorMessages.genotypeStillProcessing:message'
                            )
                          : t('reporting.common.needsAttentionYouHaventLoaded')
                      }
                      onClick={() => {
                        props.history.push({
                          pathname:
                            genotypeData && isImputationInProgress(genotypeData)
                              ? ROUTE_ASSESSMENT_HOME
                              : ROUTE_ONBOARDING_DNA,
                          state: { returningVisit: true },
                        });
                      }}
                      buttonLabel={'OK'}
                    />
                  )}

                {openBuyDialog && (
                  <>
                    {isDesktop && (
                      <Box
                        style={{
                          paddingTop: '60px',
                          paddingBottom: '20px',
                          paddingLeft: '20px',
                          paddingRight: '20px',
                        }}
                      >
                        <Typography variant={'h2'}>
                          {t('reporting.myAssessment.dnaBasedAssessment')}
                        </Typography>
                      </Box>
                    )}
                    <Grid
                      container
                      direction={isDesktop ? 'row' : 'column'}
                      justify="center"
                      alignItems="stretch"
                    >
                      <Grid item xs={12} md={6}>
                        {!isDesktop && <Divider />}
                        <div
                          style={{
                            paddingLeft: '20px',
                            paddingRight: '20px',
                            paddingBottom: '22px',
                          }}
                        >
                          <MockedDnaBasedRiskSummary
                            isDesktop={isDesktop}
                            showButton={
                              userGroups == null ||
                              !userCanSeeDnaContent(userGroups)
                            }
                            productData={productData}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          style={{
                            marginTop: isDesktop ? '10px' : '-10px',
                          }}
                        >
                          <RisksOnHumanBody
                            notPurchased={openBuyDialog}
                            title={t('reporting.dnaRisksByCategory.title')}
                            data={getMockedChartData()}
                            isDesktop={isDesktop}
                          />
                        </Box>
                      </Grid>
                      {isDesktop === false && (
                        <div
                          style={{
                            paddingLeft: '20px',
                            paddingRight: '20px',
                            paddingBottom: '80px',
                          }}
                        >
                          <Divider />
                          <MockedDnaRiskExplanation isDesktop={isDesktop} />
                        </div>
                      )}
                    </Grid>
                    {isDesktop && <Divider />}
                  </>
                )}

                {responseDnaBasedRisks &&
                  responseBloodType &&
                  openBuyDialog !== true &&
                  userCanSeeDnaContent(userGroups) &&
                  isImputationCompleted(genotypeData) && (
                    <>
                      <Grid
                        container
                        direction={isDesktop ? 'row' : 'column'}
                        justify="center"
                        alignItems="stretch"
                      >
                        <Grid item xs={12} md={6}>
                          {!isDesktop && <Divider />}
                          <div
                            style={{
                              paddingLeft: '20px',
                              paddingRight: '20px',
                              paddingTop: '20px',
                              paddingBottom: '22px',
                            }}
                          >
                            <DnaBasedRiskSummary
                              isDesktop={isDesktop}
                              riskValue={dnaTotalRisk.value}
                              dnaRiskText={dnaTotalRisk.text.toUpperCase()}
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box
                            style={{
                              borderBottom: isDesktop
                                ? 'none'
                                : '1px Solid #efefef',
                              marginTop: isDesktop ? '10px' : '-10px',
                            }}
                          >
                            <RisksOnHumanBody
                              title={t('reporting.dnaRisksByCategory.title')}
                              data={getChartData()}
                              isDesktop={isDesktop}
                              onLabelClicked={label => {
                                props.history.push({
                                  pathname: ROUTE_ASSESSMENT_DNA_TRAIT_GROUP,
                                  state: {
                                    group: label,
                                    hasFullDna: userCanSeeFullDna(userGroups),
                                    ...getStateToTransfer(),
                                  },
                                });
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      <Divider />

                      <Box
                        style={{
                          ...(isDesktop && {
                            borderBottom: '1px Solid #efefef',
                          }),
                        }}
                      >
                        <ReportsOpenTextSection
                          title={''}
                          paragraphsKeys={[
                            t('reporting.dnaRisksByCategory.paragraph1'),
                            t('reporting.dnaRisksByCategory.paragraph2'),
                          ]}
                        />
                      </Box>
                      {isDesktop === false && (
                        <Box
                          style={{
                            borderBottom: '1px Solid #efefef',
                          }}
                        >
                          {dnaTraitsAssets
                            .filter(asset =>
                              userGroups.includes('spare-dna-demo-users')
                                ? asset.alwaysVisible != null
                                : asset.alwaysVisible === true
                            )
                            .map((dnaTraitsAsset, ix) => {
                              return (
                                <RedirectPanelWithIcon
                                  key={ix}
                                  title={
                                    'blood_type' === dnaTraitsAsset.code
                                      ? 'BLOOD TYPE'
                                      : responseDnaBasedRisks[
                                          dnaTraitsAsset.code
                                        ].name
                                          .replace(' Predisposition Group', '')
                                          .toUpperCase()
                                  }
                                  icon={dnaTraitsAsset.icon}
                                  titleColor={'#2fb9da'}
                                  onClick={() => {
                                    props.history.push({
                                      pathname: ROUTE_ASSESSMENT_DNA_TRAIT_GROUP,
                                      state: {
                                        group: dnaTraitsAsset.code,
                                        hasFullDna: userCanSeeFullDna(
                                          userGroups
                                        ),
                                        ...getStateToTransfer(),
                                      },
                                    });
                                  }}
                                />
                              );
                            })}
                        </Box>
                      )}
                      <RiskReport report={responseReports} />
                    </>
                  )}

                <ReportsBottomNavigation
                  selected={reportsBottomNavigationOptions.dnaRisk}
                  onSelected={val =>
                    props.history.push({
                      pathname: val,
                      state: getStateToTransfer(),
                    })
                  }
                />
              </PageItemContainer>
            </Grid>
          </>
        )}
      </div>
    </React.Fragment>
  );
}
